import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Table } from 'react-bootstrap'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { doRequestGet, doRequestPost } from '../../../api'
import { doToast } from '../../../utils'
import { FormCustom } from '../../../components/form'
import LoadingIcon from '../../../components/loading/LoadingIcon'
import { toast } from 'react-toastify'
import SelectCustom from '../../../components/select-custom/SelectCustom'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const formatPct = (value) => `${Number(value).toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`

const getOccupancyBarColor = (pct, isActive = true) => {
  if (!isActive) return '#6c757d'
  if (pct >= 100) return '#198754'
  if (pct >= 50) return '#e85d04'
  return '#dc2f02'
}

const buildEventsOverviewChart = (occupancy) => ({
  labels: occupancy.map(e => e.name_event),
  datasets: [{
    label: '% vendido',
    data: occupancy.map(e => e.sold_pct),
    backgroundColor: occupancy.map(e => getOccupancyBarColor(e.sold_pct)),
  }],
})

const buildZoneOccupancyChart = (event) => ({
  labels: event.zones.map(z => z.name_event_zone),
  datasets: [{
    label: '% vendido',
    data: event.zones.map(z => z.sold_pct),
    backgroundColor: event.zones.map(z => getOccupancyBarColor(z.sold_pct, z.is_active)),
  }],
})

const eventsOverviewChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: '% vendido por evento',
    },
  },
  scales: {
    x: {
      max: 100,
      ticks: { callback: (v) => `${v}%` },
    },
  },
}

const buildZoneOccupancyChartOptions = (event) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: `% vendido por zona — ${event.name_event}`,
    },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const zone = event.zones[ctx.dataIndex]
          return [
            `${formatPct(ctx.parsed.y)} vendido`,
            `${zone.sold} / ${zone.capacity} tickets`,
            zone.is_active ? 'Activa' : 'Inactiva',
          ]
        },
      },
    },
  },
  scales: {
    y: {
      max: 100,
      ticks: { callback: (v) => `${v}%` },
    },
  },
})

export const ReporteVentasMensualView = () => {
  const [form, setForm] = useState({
    p_id_event: '',
  })

  const [report, setReport] = useState(null)
  const [ready, setReady] = useState(true)
  const [events, setEvents] = useState([])

  useEffect(() => {
    const reqEvents = async () => {
      const getEvents = await doRequestGet({
        url: '/worker/events'
      })
      setEvents([
        {
          id_event: -2,
          name_event: 'Todos',
          enabled_event: 1,
        },
        ...(getEvents.data || []),
      ])
    }
    reqEvents()
  }, [])

  const getReport = async () => {
    try {
      setReady(false)
      const res = await doRequestPost({
        url: '/report/sales-monthly',
        data: {
          p_id_event: form.p_id_event,
        }
      })

      if (!res?.data) {
        doToast({
          response: 'warning',
          message: 'No hay registros encontrados',
          toast
        })
        setReport(null)
        return
      }

      const hasMonthly = (res.data.rows_count ?? 0) > 0
      const hasOccupancy = (res.data.occupancy?.length ?? 0) > 0

      if (!hasMonthly && !hasOccupancy) {
        doToast({
          response: 'warning',
          message: 'No hay registros encontrados',
          toast
        })
        setReport(null)
        return
      }

      setReport(res.data)
    } catch (error) {
      doToast({
        response: 'error',
        message: 'No se pudo generar el reporte',
        toast
      })
      setReport(null)
    } finally {
      setReady(true)
    }
  }

  const chartData = useMemo(() => {
    if (!report?.series?.length) return null
    return {
      labels: report.labels || [],
      datasets: report.series.map(s => ({
        label: s.name,
        data: s.data,
        backgroundColor: s.color,
        stack: 'sales',
      })),
    }
  }, [report])

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      title: {
        display: true,
        text: report?.event?.name_event
          ? `Ventas mensuales — ${report.event.name_event}`
          : 'Ventas mensuales',
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.parsed.y ?? 0
            return `${ctx.dataset.label}: $${Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
          }
        }
      }
    },
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        ticks: {
          callback: (v) => `$${Number(v).toLocaleString('es-MX')}`
        }
      }
    }
  }), [report])

  const occupancyOverviewChart = useMemo(() => {
    if ((report?.occupancy?.length ?? 0) <= 1) return null
    return buildEventsOverviewChart(report.occupancy)
  }, [report])

  const occupancyOverviewHeight = useMemo(() => {
    const count = report?.occupancy?.length ?? 0
    return Math.max(240, count * 36)
  }, [report])

  return <div className='row'>
    <div className="col-12">
      <Card>
        <Card.Body>
          <FormCustom onSubmit={_ => {
            if (!isNaN(parseInt(form.p_id_event))) {
              getReport()
            } else {
              doToast({
                response: 'warning',
                message: 'Seleccione un evento',
                toast
              })
            }
          }} viewSubmit={false}>
            <SelectCustom
              valueId={form.p_id_event}
              keyName="event"
              options={events}
              setRow={setForm}
              row={form}
              selectText="Evento"
              onChangeAdd={_ => {
                setReport(null)
              }}
              selectPops={{
                append: [
                  <Button type="submit">
                    <i className='fa fa-search'></i>
                  </Button>
                ]
              }}
              showEnabled={false}
              isSort={false}
            />
          </FormCustom>

          {chartData ? (
            <div className="mt-4" style={{ height: 360 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          ) : null}

          {report?.table?.length ? (
            <div className="table-responsive mt-4">
              <Table bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Mes</th>
                    {report.payment_methods.map(m => (
                      <th key={m} className="text-end">{m}</th>
                    ))}
                    <th className="text-end">Total</th>
                    <th className="text-end">Tickets</th>
                  </tr>
                </thead>
                <tbody>
                  {report.table.map(row => (
                    <tr key={row.year_month}>
                      <td>{row.label}</td>
                      {row.by_method.map(cell => (
                        <td key={cell.name_format_pay} className="text-end">
                          ${Number(cell.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          <div className="small text-muted">{cell.qty} tickets</div>
                        </td>
                      ))}
                      <td className="text-end fw-bold">
                        ${Number(row.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-end">{row.qty}</td>
                    </tr>
                  ))}
                </tbody>
                {report.totals ? (
                  <tfoot>
                    <tr className="table-secondary fw-bold">
                      <td>TOTAL</td>
                      {report.totals.by_method.map(cell => (
                        <td key={cell.name_format_pay} className="text-end">
                          ${Number(cell.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          <div className="small fw-normal text-muted">{cell.qty} tickets</div>
                        </td>
                      ))}
                      <td className="text-end">
                        ${Number(report.totals.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-end">{report.totals.qty}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </Table>
            </div>
          ) : null}
          {report?.occupancy?.length ? (
            <div className="mt-4">
              <h6 className="mb-3">Ocupación por evento y zona</h6>

              {occupancyOverviewChart ? (
                <div className="mb-4" style={{ height: occupancyOverviewHeight }}>
                  <Bar
                    data={occupancyOverviewChart}
                    options={{
                      ...eventsOverviewChartOptions,
                      plugins: {
                        ...eventsOverviewChartOptions.plugins,
                        tooltip: {
                          ...eventsOverviewChartOptions.plugins.tooltip,
                          callbacks: {
                            label: (ctx) => {
                              const event = report.occupancy[ctx.dataIndex]
                              return [
                                `${formatPct(ctx.parsed.x)} vendido`,
                                `${event.sold} / ${event.capacity} tickets`,
                              ]
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              ) : null}

              {report.occupancy.map(event => (
                <div key={event.id_event} className="mb-4">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <strong>{event.name_event}</strong>
                    <span className="text-muted">
                      {event.sold} / {event.capacity} vendidos — {formatPct(event.sold_pct)}
                    </span>
                  </div>

                  {event.zones?.length ? (
                    <div
                      className="mb-3"
                      style={{ height: Math.max(220, event.zones.length * 48) }}
                    >
                      <Bar
                        data={buildZoneOccupancyChart(event)}
                        options={buildZoneOccupancyChartOptions(event)}
                      />
                    </div>
                  ) : null}

                  <div className="table-responsive">
                    <Table bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Zona</th>
                          <th>Estado</th>
                          <th className="text-end">Capacidad</th>
                          <th className="text-end">Vendidos</th>
                          <th className="text-end">% vendido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {event.zones.map(zone => (
                          <tr key={zone.id_event_zone}>
                            <td>{zone.name_event_zone}</td>
                            <td>{zone.is_active ? 'Activa' : 'Inactiva'}</td>
                            <td className="text-end">{zone.capacity}</td>
                            <td className="text-end">{zone.sold}</td>
                            <td className="text-end fw-bold">{formatPct(zone.sold_pct)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-secondary fw-bold">
                          <td colSpan={2}>Total evento</td>
                          <td className="text-end">{event.capacity}</td>
                          <td className="text-end">{event.sold}</td>
                          <td className="text-end">{formatPct(event.sold_pct)}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </div>

    {!ready && <LoadingIcon />}
  </div>
}
